// SwipeHub - Main Swipe Page
// Integrates all High Stakes Poker components

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../providers';
import { SwipeCard, KeepPileEntry, BetLevel, MatchRequest, PersonalitySuit, SwipeAllowance } from '../../types/swipe';
import { SwipeDeck } from '../../components/Swipe/SwipeDeck';
import { KeepPileHeader } from '../../components/Swipe/KeepPile/KeepPileHeader';
import { KeepPileReview } from '../../components/Swipe/KeepPile/KeepPileReview';
import { ProfilePeekDrawer } from '../../components/shared/ProfilePeekDrawer';
import { BettingInterface } from '../../components/Swipe/BettingInterface';
import { MatchResponse } from '../../components/Swipe/MatchResponse';
import { swipeService } from '../../services/Swipe/swipeService';
import {
  SwipeHubContainer,
  SwipeHubContent,
  NotificationBanner,
  NotificationIcon,
  NotificationText,
  NotificationButton
} from '../../styles/Swipe/SwipeHub.styles';
import { signalsService } from '../../services/SocialFeed/signalsService';
import { logger } from '../../utils/logger';
import { useSubscriptionContext } from '../../providers/SubscriptionProvider';

// What the deck knew when it was last closed.
//
// SwipeModal unmounts SwipeHub outright when it closes (`if (!isOpen) return
// null`), so without this every reopen starts from an empty keep pile at free-
// tier capacity and waits on three round trips before showing cards the user
// already had. Seeding from the last known values makes a reopen instant; the
// fetches below still run and correct anything that moved in the meantime.
//
// Keyed by user id, and dropped on sign-out, so one account never renders
// another's pile for the frame before the network answers.
type DeckSessionCache = {
  userId: string;
  keepPile: KeepPileEntry[];
  maxKeepPileSize: number;
  swipeAllowance: SwipeAllowance | null;
};
let lastDeckSession: DeckSessionCache | null = null;

export const SwipeHub: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuthContext();
  const currentUserId = user?.id ?? '';
  const { tier } = useSubscriptionContext();
  const cachedSession = lastDeckSession?.userId === currentUserId ? lastDeckSession : null;

  // Today's swipe allowance, held here so the fixed header can show it while
  // the deck spends it. Server-derived, so it survives a reload and a second
  // device rather than resetting with the component.
  const [swipeAllowance, setSwipeAllowance] = useState<SwipeAllowance | null>(
    cachedSession?.swipeAllowance ?? null,
  );

  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    swipeService.getDailyLimitStatus(currentUserId)
      .then((next) => { if (!cancelled && next) setSwipeAllowance(next); })
      .catch((err) => logger.log('Could not read swipe allowance:', err));
    return () => { cancelled = true; };
    // Keyed on tier so an upgrade raises the count straight away.
  }, [currentUserId, tier]);

  // Keep Pile State
  const [keepPile, setKeepPile] = useState<KeepPileEntry[]>(cachedSession?.keepPile ?? []);
  // Capacity is a subscription perk: free 5, Premium 15, Premium Plus 20.
  // Starts at whatever this account last saw, or the free size on a first
  // open, and is replaced by whatever the server reports — guessing high would
  // show a subscriber's allowance to everyone for the moment before the
  // response lands, and let a free user start an add the backend then refuses.
  const [maxKeepPileSize, setMaxKeepPileSize] = useState(cachedSession?.maxKeepPileSize ?? 5);

  // Hand this session's state to the next open of the modal.
  useEffect(() => {
    // Drop the cache only on a settled sign-out. Keying this on an empty user
    // id instead threw the pile away every time auth was momentarily between
    // states — which is exactly when the modal remounts, so the cache was
    // being cleared by the very reopen it existed to speed up.
    if (!authLoading && !isAuthenticated) {
      lastDeckSession = null;
      return;
    }
    if (!currentUserId) return;
    lastDeckSession = { userId: currentUserId, keepPile, maxKeepPileSize, swipeAllowance };
  }, [currentUserId, isAuthenticated, authLoading, keepPile, maxKeepPileSize, swipeAllowance]);

  // Modal States
  const [showKeepPileReview, setShowKeepPileReview] = useState(false);
  const [peekEntry, setPeekEntry] = useState<KeepPileEntry | null>(null);
  const [selectedEntryForBetting, setSelectedEntryForBetting] = useState<KeepPileEntry | null>(null);
  const [incomingMatchRequest, setIncomingMatchRequest] = useState<MatchRequest | null>(null);

  // Notification State
  const [notification, setNotification] = useState<{
    type: 'match' | 'request' | 'info' | 'warning';
    message: string;
  } | null>(null);

  // Show notification (defined early so other callbacks can use it)
  const showNotification = useCallback((type: 'match' | 'request' | 'info' | 'warning', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  }, []);

  // Fetch bet requests that are currently waiting on this user to respond,
  // surfacing the most urgent one in the MatchResponse (Fold/Call/Raise) UI.
  const fetchActionableBetRequests = useCallback(async () => {
    if (!currentUserId) return;
    try {
      const requests = await swipeService.getBetRequests('incoming');
      setIncomingMatchRequest(requests[0] ?? null);
    } catch (error) {
      console.error('Error fetching incoming bet requests:', error);
    }
  }, [currentUserId]);

  // Load incoming bet requests on mount / when the user becomes available
  useEffect(() => {
    fetchActionableBetRequests();
  }, [fetchActionableBetRequests]);

  // Capacity for this account's tier. Refreshed alongside the pile itself.
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    swipeService.getKeepPileAllowance()
      .then((allowance) => {
        if (!cancelled && allowance?.maxSize) setMaxKeepPileSize(allowance.maxSize);
      })
      .catch((err) => console.error('Failed to load keep pile allowance:', err));
    return () => { cancelled = true; };
    // Keyed on tier so an upgrade widens the pile straight away.
  }, [currentUserId, tier]);

  // Load the persisted keep pile so it survives reloads / other devices.
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    swipeService.getKeepPileEntries()
      .then((entries) => { if (!cancelled) setKeepPile(entries); })
      .catch((err) => console.error('Failed to load keep pile:', err));
    return () => { cancelled = true; };
  }, [currentUserId]);

  // One swipe spent. Decremented rather than refetched: a round trip per
  // swipe would put the network back in the gesture's path, which is what the
  // deck was just changed to avoid.
  const handleSwipeSpent = useCallback(() => {
    setSwipeAllowance(prev => (prev && !prev.unlimited && prev.remaining !== null)
      ? { ...prev, used: prev.used + 1, remaining: Math.max(0, prev.remaining - 1) }
      : prev);
  }, []);

  // The server refused a swipe. Trust it over the local count, which can be
  // behind if swipes were spent on another device.
  const handleSwipeLimitReached = useCallback(() => {
    // Never applied to an unlimited plan. The server cannot refuse a Premium
    // Plus swipe for the limit, so if this ever fired for one it would be
    // acting on a rejection that meant something else entirely.
    setSwipeAllowance(prev => (prev && !prev.unlimited
      ? { ...prev, remaining: 0, used: prev.dailyLimit ?? prev.used }
      : prev));
  }, []);

  // Handle adding card to keep pile
  const handleKeepCard = useCallback((card: SwipeCard) => {
    logger.log('🎴 Keep card triggered:', card.teaser.name);

    if (keepPile.length >= maxKeepPileSize) {
      logger.log('⚠️ Keep pile is full');
      showNotification('warning', `Keep pile is full (${maxKeepPileSize} cards). Review your cards before adding more.`);
      return;
    }

    // Calculate card suit based on user name (same algorithm as SwipeCard)
    const SUITS: Array<'hearts' | 'diamonds' | 'clubs' | 'spades'> = ['spades', 'hearts', 'diamonds', 'clubs'];
    const hash = card.user.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const cardSuit = SUITS[hash % SUITS.length] as 'hearts' | 'diamonds' | 'clubs' | 'spades';

    const newEntry: KeepPileEntry = {
      id: `keep-${Date.now()}`,
      profileId: card.user.id,
      profile: {
        ...card.user,
        firstName: card.user.firstName || card.user.name.split(' ')[0],
        personalityType: card.user.personality?.[0] || 'Unknown',
        compatibilityScore: card.teaser.matchPercentage ?? card.revealed.compatibility ?? 0
      },
      cardSuit: cardSuit, // Store the suit from the swipe card
      addedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString(), // 72 hours
      priority: keepPile.length + 1
    };

    logger.log('✅ Adding to keep pile:', newEntry);
    setKeepPile(prev => [...prev, newEntry]);

    // Keeping a card is the deck's strongest positive judgment — the same act
    // the heart expresses in the feeds, so it records as profile_like with
    // surface='swipe' rather than a swipe-specific type.
    //
    // ONE signal, not two. This used to emit keep_pile_add alongside it, so a
    // single gesture contributed 1.9 to affinity while an equally enthusiastic
    // heart in the Matches feed contributed 1.0 — which quietly biased ranking
    // toward whoever the user happened to meet in the deck. Keep pile
    // membership is already state in the keep_pile table; it does not need to
    // be a second engagement event.
    signalsService.track({
      signalType: 'profile_like',
      targetUserId: card.user.id,
      surface: 'swipe',
      metadata: { compatibility: card.teaser?.matchPercentage ?? null, via: 'keep' },
    });

    // Persist to the DB keep pile. On failure, roll back the optimistic add.
    swipeService.addToKeepPile(currentUserId, card.user.id).catch((err) => {
      // Log what the server actually said, not just that something failed.
      // 'Could not save to keep pile' is unactionable on its own: an expired
      // session, a missing table and a rejected profile id all read the same.
      // The profile id is in here because a deck that fell back to mock data
      // carries ids like 'user-1', which keep_pile.profile_id (a UUID FK to
      // public.users) cannot accept — a 500 that has nothing to do with the
      // keep pile itself.
      console.error('Failed to save card to keep pile:', {
        status: err?.status ?? null,
        code: err?.code || null,
        message: err?.message,
        profileId: card.user.id,
      });
      setKeepPile(prev => prev.filter(e => e.id !== newEntry.id));
      // The server is the authority on capacity — the local check above can be
      // stale if the pile was filled on another device. Show its message, which
      // names the plan's limit, instead of 'please try again' for a retry that
      // cannot succeed.
      const full = err?.code === 'KEEP_PILE_FULL' || err?.status === 403;
      // Same reasoning for a dead session: retrying is hopeless until they
      // sign in again, so say that instead of inviting the retry.
      const signedOut = err?.status === 401;
      showNotification('warning',
        full ? (err?.message ?? 'Your keep pile is full.')
          : signedOut ? 'Your session expired. Sign in again to keep cards.'
            : 'Could not save to keep pile. Please try again.');
    });
  }, [keepPile, maxKeepPileSize, currentUserId, showNotification]);

  // Handle discarding card
  const handleDiscardCard = useCallback((card: SwipeCard) => {
    logger.log('Discarded:', card.user.name);
    signalsService.track({ signalType: 'profile_pass', targetUserId: card.user.id, surface: 'swipe' });
  }, []);

  // Handle skipping card
  const handleSkipCard = useCallback((card: SwipeCard) => {
    logger.log('Skipped:', card.user.name);
    // A skip defers rather than rejects, so it is only mildly negative — worth
    // distinguishing from a discard when reading the log back.
    signalsService.track({ signalType: 'swipe_skip', targetUserId: card.user.id, surface: 'swipe' });
  }, []);

  // Handle viewing profile — slides the profile in over the keep pile instead
  // of navigating away, so the player never loses their place in the deck.
  const handleViewProfile = useCallback((profileId: string) => {
    const entry = keepPile.find(e => e.profileId === profileId);
    if (entry) setPeekEntry(entry);
  }, [keepPile]);

  // Handle removing from keep pile
  const handleRemoveFromKeepPile = useCallback((entryId: string) => {
    const entry = keepPile.find(e => e.id === entryId);
    if (entry) {
      // A reversal of earlier interest, which says more than never having
      // kept them at all.
      signalsService.track({
        signalType: 'keep_pile_remove',
        targetUserId: entry.profileId,
        surface: 'swipe',
      });
    }
    setKeepPile(prev => prev.filter(e => e.id !== entryId));
    // If the removed card is the one being previewed, slide the drawer away.
    setPeekEntry(prev => (prev?.id === entryId ? null : prev));
    if (entry) {
      swipeService.removeFromKeepPile(currentUserId, entry.profileId).catch((err) =>
        console.error('Failed to remove from keep pile:', err));
      showNotification('info', `Removed ${entry.profile.firstName} from keep pile`);
    }
  }, [keepPile, currentUserId, showNotification]);

  /**
   * Whether the betting interface was opened from inside the keep pile.
   *
   * The pile has to close to make room for it, and without remembering this
   * the way back was always the deck — so playing one card ended a browsing
   * session that was nowhere near finished.
   */
  const returnToKeepPile = useRef(false);

  /** Puts the pile back if that is where the viewer came from. */
  const restoreKeepPile = useCallback((remaining: number) => {
    const shouldReturn = returnToKeepPile.current && remaining > 0;
    returnToKeepPile.current = false;
    if (shouldReturn) setShowKeepPileReview(true);
  }, []);

  // Handle going to betting interface
  const handleGoToMatching = useCallback((entry: KeepPileEntry) => {
    returnToKeepPile.current = showKeepPileReview;
    setSelectedEntryForBetting(entry);
    setPeekEntry(null);
    setShowKeepPileReview(false);
  }, [showKeepPileReview]);

  // Handle submitting bet
  const handleSubmitBet = useCallback(async (betLevel: BetLevel, message: string) => {
    if (!selectedEntryForBetting) return;

    const entry = selectedEntryForBetting;
    try {
      await swipeService.proposeBetRequest(entry.profileId, betLevel, message);

      // Remove from keep pile after playing (locally + in the DB)
      setKeepPile(prev => prev.filter(e => e.id !== entry.id));
      swipeService.removeFromKeepPile(currentUserId, entry.profileId).catch((err) =>
        console.error('Failed to remove kept card after bet:', err));

      // Close modal
      setSelectedEntryForBetting(null);

      // Back to the pile, if that is where this started. Only while cards are
      // left: reopening an empty pile to show an empty state is not "carry on
      // browsing", it is a dead end with an extra step.
      restoreKeepPile(keepPile.filter(e => e.id !== entry.id).length);

      // Show success notification
      showNotification('match', `Your card has been played! Now we wait for ${entry.profile.firstName}'s response...`);
    } catch (error) {
      console.error('Error submitting bet:', error);
      showNotification('warning', 'Failed to send match request. Please try again.');
    }
  }, [selectedEntryForBetting, currentUserId, showNotification, keepPile, restoreKeepPile]);

  // Handle responding to match request
  const handleRespondToMatch = useCallback(async (action: 'accept' | 'decline' | 'counter', counterBet?: BetLevel) => {
    if (!incomingMatchRequest) return;

    try {
      await swipeService.respondToBetRequest(incomingMatchRequest.id, action, counterBet);

      // Close modal
      setIncomingMatchRequest(null);

      // Show notification based on action
      if (action === 'accept') {
        showNotification('match', "🎉 It's a Match! You can now start chatting!");
        setTimeout(() => {
          navigate('/messages');
        }, 2000);
      } else if (action === 'decline') {
        showNotification('info', 'Match declined');
      } else {
        showNotification('info', 'Counter-offer sent! Waiting for their response...');
      }

      // Surface the next actionable request, if any.
      fetchActionableBetRequests();
    } catch (error) {
      console.error('Error responding to match:', error);
      showNotification('warning', 'Failed to send response. Please try again.');
    }
  }, [incomingMatchRequest, navigate, showNotification, fetchActionableBetRequests]);

  // Handle card exhaustion
  const handleCardExhausted = useCallback(() => {
    showNotification('info', 'No more cards! Check back later or adjust your preferences.');
  }, [showNotification]);

  return (
    <SwipeHubContainer>
      {/* Keep Pile Header - Always visible */}
      <KeepPileHeader
        entries={keepPile}
        maxCount={maxKeepPileSize}
        swipesRemaining={swipeAllowance && !swipeAllowance.unlimited ? swipeAllowance.remaining : null}
        dailySwipeLimit={swipeAllowance?.dailyLimit ?? null}
        unlimitedSwipes={swipeAllowance?.unlimited ?? false}
        onViewKeepPile={() => setShowKeepPileReview(true)}
      />

      {/* Notification Banner */}
      {notification && (
        <NotificationBanner $type={notification.type}>
          <NotificationIcon>
            {notification.type === 'match' && '🎉'}
            {notification.type === 'request' && '💌'}
            {notification.type === 'info' && 'ℹ️'}
            {notification.type === 'warning' && '⚠️'}
          </NotificationIcon>
          <NotificationText>{notification.message}</NotificationText>
          <NotificationButton onClick={() => setNotification(null)}>
            ×
          </NotificationButton>
        </NotificationBanner>
      )}

      {/* Incoming Match Request Badge */}
      {incomingMatchRequest && (
        <NotificationBanner $type="request">
          <NotificationIcon>💌</NotificationIcon>
          <NotificationText>You have an incoming match request!</NotificationText>
          <NotificationButton onClick={() => {/* Open match response modal */ }}>
            View
          </NotificationButton>
        </NotificationBanner>
      )}

      {/* Main Content - Swipe Deck */}
      <SwipeHubContent>
        <SwipeDeck
          userId={currentUserId}
          onKeep={handleKeepCard}
          onDiscard={handleDiscardCard}
          onSkip={handleSkipCard}
          keepPileCount={keepPile.length}
          maxKeepPileSize={maxKeepPileSize}
          onCardExhausted={handleCardExhausted}
          onShowNotification={showNotification}
          allowance={swipeAllowance}
          onSwipeSpent={handleSwipeSpent}
          onSwipeLimitReached={handleSwipeLimitReached}
        />
      </SwipeHubContent>

      {/* Keep Pile Review Modal */}
      {showKeepPileReview && (
        <KeepPileReview
          entries={keepPile}
          // The same allowance the deck already resolved from the account's
          // tier, rather than a second guess at it.
          maxSize={maxKeepPileSize}
          onClose={() => setShowKeepPileReview(false)}
          onRemove={handleRemoveFromKeepPile}
          onViewProfile={handleViewProfile}
          onGoToMatching={handleGoToMatching}
        />
      )}

      {/* Profile Peek Drawer — slides in over the Keep Pile Review */}
      <ProfilePeekDrawer
        target={peekEntry && {
          profileId: peekEntry.profileId,
          firstName: peekEntry.profile.firstName,
          age: peekEntry.profile.age,
          compatibilityScore: peekEntry.profile.compatibilityScore,
          context: 'From your keep pile',
        }}
        onClose={() => setPeekEntry(null)}
        backLabel="Keep Pile"
        actions={peekEntry ? [
          {
            key: 'play',
            label: '🎯 Play This Card',
            variant: 'primary',
            onClick: () => handleGoToMatching(peekEntry),
          },
          {
            key: 'remove',
            label: '✕',
            variant: 'danger',
            ariaLabel: `Remove ${peekEntry.profile.firstName} from keep pile`,
            onClick: () => handleRemoveFromKeepPile(peekEntry.id),
          },
        ] : []}
        onNotify={showNotification}
      />

      {/* Betting Interface Modal */}
      {selectedEntryForBetting && (
        <BettingInterface
          entry={selectedEntryForBetting}
          onClose={() => {
            setSelectedEntryForBetting(null);
            // Nothing was played, so the pile is unchanged and the viewer is
            // exactly where they were.
            restoreKeepPile(keepPile.length);
          }}
          onSubmit={handleSubmitBet}
        />
      )}

      {/* Match Response Modal */}
      {incomingMatchRequest && (
        <MatchResponse
          matchRequest={incomingMatchRequest}
          onResponse={handleRespondToMatch}
        />
      )}
    </SwipeHubContainer>
  );
};

